import { useCallback, useEffect, useRef, useState } from 'react';
import type { Decimal } from '@insio/decimal';
import type { InvoiceLineAmounts, VatMode } from '../../../../graphql/generated/graphql';

/** Fields a line needs to expose for the recompute callback. */
export interface RecomputeLineFields {
  quantity?: Decimal | null;
  unitPrice?: Decimal | null;
  discountPercent?: Decimal | null;
  vatRate?: Decimal | null;
  vatMode?: VatMode | null;
  recyclingFee?: Decimal | null;
  /** Typed net total — lets the backend reverse-derive unitPrice / VAT. */
  totalAmountNoVat?: Decimal | null;
}

/** Backend amounts for one line; resolving `null` (or rejecting) reports a failure. */
export type RecomputeInvoiceLineFn = (
  line: RecomputeLineFields,
  isUnitPriceInclVat: boolean
) => Promise<InvoiceLineAmounts | null>;

interface UseLineAmountsRecomputeArgs {
  recompute: RecomputeInvoiceLineFn | undefined;
  isUnitPriceInclVat: boolean;
  /** Debounce window for change-driven recomputes. */
  debounceMs?: number;
}

interface LineAmountsRecompute {
  /** Latest settled amounts for `lineIndex`, or `null` while pending / never run,
   * or after a failure. */
  getComputed: (lineIndex: number) => InvoiceLineAmounts | null;
  /** Whether a settled result exists but was computed for inputs other than `line`'s. */
  isStale: (lineIndex: number, line: RecomputeLineFields) => boolean;
  /** Amounts for exactly `line`'s inputs: settled, in flight, or fired now. `null` on failure,
   * after which the line is stale (no inputs match), so the next call fires again. */
  ensureFresh: (lineIndex: number, line: RecomputeLineFields) => Promise<InvoiceLineAmounts | null>;
  /** Settle `amounts` as the result for `line`'s inputs without a request. */
  adopt: (lineIndex: number, line: RecomputeLineFields, amounts: InvoiceLineAmounts) => void;
  /** Schedule a debounced recompute. */
  schedule: (lineIndex: number, line: RecomputeLineFields) => void;
  /** Run recompute immediately (used on blur to commit pending changes). */
  runNow: (lineIndex: number, line: RecomputeLineFields) => void;
  /** Drop computed state + pending timers for a line. Use when a line is deleted. */
  clearForLine: (lineIndex: number) => void;
}

/** A result together with the fingerprint of the inputs that produced it. */
interface SettledResult {
  /** `null` after a failed request: matches no inputs, so the line stays stale. */
  fingerprint: string | null;
  amounts: InvoiceLineAmounts | null;
}

/** A rejected request, or a `null` result (the adapters' way of reporting an error). */
const FAILED: SettledResult = { fingerprint: null, amounts: null };

/** The latest request of a line; `promise` never rejects. */
interface InFlightRequest {
  fingerprint: string;
  requestId: number;
  promise: Promise<InvoiceLineAmounts | null>;
}

type SettledByLine = Record<number, SettledResult>;

const NUMERIC_FIELDS: (keyof RecomputeLineFields)[] = [
  'quantity',
  'unitPrice',
  'discountPercent',
  'vatRate',
  'recyclingFee',
  'totalAmountNoVat'
];

/** Identity of the recompute inputs of a line: equal fingerprints get equal amounts. */
export function lineFingerprint(line: RecomputeLineFields, isUnitPriceInclVat: boolean): string {
  const parts = NUMERIC_FIELDS.map(f => {
    const v = line[f] as Decimal | null | undefined;
    return v == null ? '' : v.toString();
  });
  parts.push(line.vatMode ?? '');
  parts.push(isUnitPriceInclVat ? '1' : '0');
  return parts.join('|');
}

/** Drives the `computeInvoiceLineAmounts` query per line (debounced on change, immediate on
 * blur) and keeps each line's latest result with the fingerprint of the inputs behind it. */
export function useLineAmountsRecompute({
  recompute,
  isUnitPriceInclVat,
  // Below typical inter-key pauses a change-driven recompute fires per
  // keystroke; blur still recomputes immediately, so a wider window only
  // delays mid-typing hints.
  debounceMs = 400
}: UseLineAmountsRecomputeArgs): LineAmountsRecompute {
  const [settled, setSettled] = useState<SettledByLine>({});
  // Mirror for synchronous reads from `ensureFresh` after an await.
  const settledRef = useRef<SettledByLine>(settled);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  /** Fingerprint of the last fired request per line; dedupes `schedule` / `runNow`. */
  const lastFingerprintRef = useRef<Map<number, string>>(new Map());
  /** Monotonic per-line request id so a late response can be discarded if a
   * newer request has been issued. */
  const requestIdRef = useRef<Map<number, number>>(new Map());
  const inFlightRef = useRef<Map<number, InFlightRequest>>(new Map());

  // Recompute trigger is captured per-call by stored closures; keep a ref so
  // pending timers always see the latest `recompute` / `isUnitPriceInclVat`.
  const recomputeRef = useRef(recompute);
  const inclVatRef = useRef(isUnitPriceInclVat);
  useEffect(() => {
    recomputeRef.current = recompute;
  }, [recompute]);
  useEffect(() => {
    inclVatRef.current = isUnitPriceInclVat;
  }, [isUnitPriceInclVat]);

  const updateSettled = useCallback((update: (prev: SettledByLine) => SettledByLine) => {
    const next = update(settledRef.current);
    if (next === settledRef.current) return;
    settledRef.current = next;
    setSettled(next);
  }, []);

  const settle = useCallback(
    (lineIndex: number, result: SettledResult) =>
      updateSettled(prev => ({ ...prev, [lineIndex]: result })),
    [updateSettled]
  );

  const cancelTimer = useCallback((lineIndex: number) => {
    const t = timersRef.current.get(lineIndex);
    if (t != null) {
      clearTimeout(t);
      timersRef.current.delete(lineIndex);
    }
  }, []);

  /** Fire a request unless `force` is off and the inputs equal the last fired ones. */
  const fire = useCallback(
    (
      lineIndex: number,
      line: RecomputeLineFields,
      force: boolean
    ): Promise<InvoiceLineAmounts | null> | null => {
      const fn = recomputeRef.current;
      if (!fn) return null;
      const incl = inclVatRef.current;
      const fp = lineFingerprint(line, incl);
      if (!force && lastFingerprintRef.current.get(lineIndex) === fp) return null;
      lastFingerprintRef.current.set(lineIndex, fp);

      const requestId = (requestIdRef.current.get(lineIndex) ?? 0) + 1;
      requestIdRef.current.set(lineIndex, requestId);
      const isLatest = () => requestIdRef.current.get(lineIndex) === requestId;
      const promise = (async () => {
        try {
          const amounts = await fn(line, incl);
          if (isLatest()) settle(lineIndex, amounts ? { fingerprint: fp, amounts } : FAILED);
          return amounts;
        } catch {
          if (isLatest()) settle(lineIndex, FAILED);
          return null;
        }
      })();
      inFlightRef.current.set(lineIndex, { fingerprint: fp, requestId, promise });
      // Registered before any caller's `await`, so the entry is gone when they resume.
      void promise.then(() => {
        if (inFlightRef.current.get(lineIndex)?.requestId === requestId) {
          inFlightRef.current.delete(lineIndex);
        }
      });
      return promise;
    },
    [settle]
  );

  const schedule: LineAmountsRecompute['schedule'] = useCallback(
    (lineIndex, line) => {
      if (!recomputeRef.current) return;
      cancelTimer(lineIndex);
      const t = setTimeout(() => {
        timersRef.current.delete(lineIndex);
        void fire(lineIndex, line, false);
      }, debounceMs);
      timersRef.current.set(lineIndex, t);
    },
    [cancelTimer, debounceMs, fire]
  );

  const runNow: LineAmountsRecompute['runNow'] = useCallback(
    (lineIndex, line) => {
      cancelTimer(lineIndex);
      void fire(lineIndex, line, false);
    },
    [cancelTimer, fire]
  );

  const ensureFresh: LineAmountsRecompute['ensureFresh'] = useCallback(
    (lineIndex, line) => {
      if (!recomputeRef.current) return Promise.resolve(null);
      const fp = lineFingerprint(line, inclVatRef.current);
      const current = settledRef.current[lineIndex];
      if (current?.fingerprint === fp) return Promise.resolve(current.amounts);
      const inFlight = inFlightRef.current.get(lineIndex);
      if (inFlight?.fingerprint === fp) return inFlight.promise;
      return fire(lineIndex, line, true) ?? Promise.resolve(null);
    },
    [fire]
  );

  const adopt: LineAmountsRecompute['adopt'] = useCallback(
    (lineIndex, line, amounts) =>
      settle(lineIndex, { fingerprint: lineFingerprint(line, inclVatRef.current), amounts }),
    [settle]
  );

  const clearForLine: LineAmountsRecompute['clearForLine'] = useCallback(
    lineIndex => {
      cancelTimer(lineIndex);
      lastFingerprintRef.current.delete(lineIndex);
      // Bumped, not deleted: a restarted id would let an older response count as latest.
      requestIdRef.current.set(lineIndex, (requestIdRef.current.get(lineIndex) ?? 0) + 1);
      inFlightRef.current.delete(lineIndex);
      updateSettled(prev => {
        if (!(lineIndex in prev)) return prev;
        const next = { ...prev };
        delete next[lineIndex];
        return next;
      });
    },
    [cancelTimer, updateSettled]
  );

  const getComputed: LineAmountsRecompute['getComputed'] = useCallback(
    lineIndex => settled[lineIndex]?.amounts ?? null,
    [settled]
  );

  const isStale: LineAmountsRecompute['isStale'] = useCallback(
    (lineIndex, line) => {
      const result = settled[lineIndex];
      return result != null && result.fingerprint !== lineFingerprint(line, isUnitPriceInclVat);
    },
    [settled, isUnitPriceInclVat]
  );

  // Clear timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return { getComputed, isStale, ensureFresh, adopt, schedule, runNow, clearForLine };
}
